import { withPricingMutationLock, pricingBatchScope } from '../utils/pricingMutationLock';
import axios from 'axios';
import { getToken } from './auth';
import { invalidateProducts } from '../utils/productEventsHub';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.chamafruits.com';

const withAuth = () => ({
  headers: { Authorization: `Bearer ${getToken()}` },
  withCredentials: true,
});

// 📦 Products
export const getProducts = async (branchId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/`, { params })).data;

export const getReferenceCosts = async (branchId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/reference-costs`, withAuth())).data;

export const getProductPresentations = async (branchId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/presentations`, { ...withAuth(), params })).data;

export const getPriceListConfigurations = async (branchId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/configurations`, { ...withAuth(), params })).data;

export const getPriceListBranchOverview = async (branchId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/branch-overview`, withAuth())).data;


export const updateProductPriceListConfiguration = async (branchId, productId, data) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/product/price-list/products/${productId}`, data, withAuth())).data;

export const updateVarietyPriceListConfiguration = async (branchId, varietyId, data) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/product/price-list/varieties/${varietyId}`, data, withAuth())).data;

export const updatePriceListFormat = async (branchId, varietyPriceId, data) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/product/price-list/formats/${varietyPriceId}`, data, withAuth())).data;

export const updatePriceListFormatSettings = async (branchId, varietyPriceId, data) => withPricingMutationLock(`branch:${branchId}:format:${varietyPriceId}:settings`, async () => {
  if (!data || !Object.keys(data).length) throw new Error('No hay cambios para guardar.');
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/price-list/formats/${varietyPriceId}/settings`, data, withAuth())).data;
  invalidateProducts(branchId, { action: 'price-list-settings-updated', priceId: varietyPriceId });
  return response;
});

export const updateQuickPriceListFormat = async (branchId, varietyPriceId, data) => withPricingMutationLock(`branch:${branchId}:format:${varietyPriceId}:quick`, async () => {
  if (!data || !Object.keys(data).length) throw new Error('No hay cambios para guardar.');
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/price-list/formats/${varietyPriceId}/quick`, data, withAuth())).data;
  invalidateProducts(branchId, { action: 'quick-price-updated', priceId: varietyPriceId });
  return response;
});

export const previewPriceListOffers = async (branchId, data) => (
  await axios.post(`${API_URL}/branch/${branchId}/product/price-list/offers/preview`, data, withAuth())
).data;

export const createPriceListOffers = async (branchId, data) => withPricingMutationLock(pricingBatchScope(branchId, 'offers', { formatIds: data?.varietyPriceIds }), async () => {
  const response = (await axios.post(`${API_URL}/branch/${branchId}/product/price-list/offers/bulk`, data, withAuth())).data;
  invalidateProducts(branchId, { action: 'catalog-structure-updated', priceIds: response?.offers?.map((offer) => offer.varietyPriceId) });
  return response;
});

export const editPriceListOffer = async (branchId, varietyPriceId, data) => withPricingMutationLock(`branch:${branchId}:format:${varietyPriceId}:quick`, async () => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/price-list/offers/${varietyPriceId}`, data, withAuth())).data;
  invalidateProducts(branchId, { action: 'offer-updated', priceIds: [varietyPriceId] });
  return response;
});

export const endPriceListOffer = async (branchId, varietyPriceId, data) => withPricingMutationLock(`branch:${branchId}:format:${varietyPriceId}:quick`, async () => {
  const response = (await axios.post(`${API_URL}/branch/${branchId}/product/price-list/offers/${varietyPriceId}/end`, data, withAuth())).data;
  invalidateProducts(branchId, { action: 'offer-updated', priceIds: [varietyPriceId] });
  return response;
});

export const updateOfferAvailability = async (branchId, varietyPriceId, data) => withPricingMutationLock(`branch:${branchId}:format:${varietyPriceId}:quick`, async () => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/price-list/formats/${varietyPriceId}/offer-availability`, data, withAuth())).data;
  invalidateProducts(branchId, { action: 'availability-updated', priceId: varietyPriceId });
  return response;
});

export const updateQuickPriceListFormatsBulk = async (branchId, data) => withPricingMutationLock(pricingBatchScope(branchId, 'bulk', data), async () => {
  const response = (await axios.post(
    `${API_URL}/branch/${branchId}/product/price-list/formats/bulk`,
    data,
    withAuth()
  )).data;
  invalidateProducts(branchId, {
    action: 'quick-prices-bulk-updated',
    priceIds: response?.affectedVarietyPriceIds || data?.formatIds,
    ...(response?.pricingActivity ? { activityId: response.pricingActivity.id, companyId: response.pricingActivity.companyId } : {}),
  });
  return response;
});

export const updateQuickPriceListFormatLocks = async (branchId, data) => withPricingMutationLock(pricingBatchScope(branchId, 'locks', data), async () => {
  const response = (await axios.patch(
    `${API_URL}/branch/${branchId}/product/price-list/formats/locks`,
    data,
    withAuth()
  )).data;
  invalidateProducts(branchId, {
    action: 'quick-price-locks-updated',
    priceIds: response?.affectedVarietyPriceIds || data?.formatIds,
    ...(response?.pricingActivity ? { activityId: response.pricingActivity.id, companyId: response.pricingActivity.companyId } : {}),
  });
  return response;
});

export const recalculateQuickPriceListFormats = async (branchId, data) => withPricingMutationLock(pricingBatchScope(branchId, 'recalculate', data), async () => {
  const response = (await axios.post(
    `${API_URL}/branch/${branchId}/product/price-list/formats/recalculate`,
    data,
    withAuth()
  )).data;
  invalidateProducts(branchId, {
    action: 'quick-prices-recalculated',
    priceIds: response?.affectedVarietyPriceIds || data?.formatIds,
    ...(response?.pricingActivity ? { activityId: response.pricingActivity.id, companyId: response.pricingActivity.companyId } : {}),
  });
  return response;
});

export const previewQuickPriceCostOverride = async (branchId, varietyPriceId, data) =>
  (await axios.post(
    `${API_URL}/branch/${branchId}/product/price-list/formats/${varietyPriceId}/cost-override/preview`,
    data,
    withAuth()
  )).data;

export const applyQuickPriceCostOverride = async (branchId, varietyPriceId, data) => {
  const response = (await axios.post(
    `${API_URL}/branch/${branchId}/product/price-list/formats/${varietyPriceId}/cost-override/apply`,
    data,
    withAuth()
  )).data;
  invalidateProducts(branchId, {
    action: 'quick-price-cost-override-applied',
    priceId: varietyPriceId,
  });
  return response;
};

export const updateQuickPriceListProductAvailability = async (branchId, productId, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/price-list/products/${productId}/availability`, data, withAuth())).data;
  invalidateProducts(branchId, { action: 'quick-product-availability-updated', productId });
  return response;
};

export const getPriceListAvailabilityBranches = async (branchId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/availability-branches`, withAuth())).data;

export const updatePriceListAvailabilityBranches = async (branchId, productId, data) => withPricingMutationLock(`branch:${branchId}:product:${productId}:availability`, async () => {
  const response = (await axios.post(`${API_URL}/branch/${branchId}/product/price-list/products/${productId}/availability-branches`, data, withAuth())).data;
  response.branches.forEach((branch) => {
    if (branch.updatedCount) invalidateProducts(branch.branchId, { action: 'availability-updated', productId, priceIds: branch.priceIds });
  });
  return response;
});

export const getProductUnitEquivalences = async (branchId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/unit-equivalences`, withAuth())).data;

export const updateVarietyInventorySettings = async (branchId, varietyId, data) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/product/variety/${varietyId}/inventory-settings`, data, withAuth())).data;

export const updateVarietyPriceEquivalence = async (branchId, varietyPriceId, data) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-price/${varietyPriceId}/equivalence`, data, withAuth())).data;

export const rebuildInventoryProjection = async (branchId) =>
  (await axios.post(`${API_URL}/branch/${branchId}/product/unit-equivalences/rebuild-inventory`, {}, withAuth())).data;

export const downloadPriceListPdf = async (branchId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/pdf`, {
    ...withAuth(),
    params,
    responseType: 'blob',
  })).data;

export const requestPriceListPdfJob = async (branchId, payload = {}) =>
  (await axios.post(`${API_URL}/branch/${branchId}/product/price-list/pdf/jobs`, payload, withAuth())).data;

export const getPriceListPdfJobStatus = async (branchId, jobId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/pdf/jobs/${jobId}`, withAuth())).data;

export const downloadPriceListPdfJob = async (branchId, jobId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/pdf/download/${jobId}`, {
    ...withAuth(),
    responseType: 'blob',
  })).data;

export const buildPublicPriceListPdfUrl = (
  branchId,
  jobId,
  publicAccessToken,
  { download = false } = {}
) => {
  if (!publicAccessToken) throw new Error('A signed PDF access token is required.');
  const url = new URL(`${API_URL}/branch/${branchId}/product/price-list/pdf/public/${jobId}`);
  url.searchParams.set('token', publicAccessToken);
  if (download) url.searchParams.set('download', '1');
  return url.toString();
};

export const createProduct = async (branchId, product) => {
  product.productName = product.productName.trim().toLowerCase();
  product.productName = product.productName.charAt(0).toUpperCase() + product.productName.slice(1);
  const data = (await axios.post(`${API_URL}/branch/${branchId}/product`, product, withAuth())).data;
  invalidateProducts(branchId, { action: 'create-product', productId: data?.id, product: data });
  return data;
};

export const updateProduct = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/${id}`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'update-product',
    productId: id,
    product: response || { id, ...data },
  });
  return response;
};

export const deleteProduct = async (branchId, id) => {
  const response = (await axios.delete(`${API_URL}/branch/${branchId}/product/${id}`, withAuth())).data;
  invalidateProducts(branchId, { action: 'delete-product', productId: id });
  return response;
};

// 🌱 Varieties
export const addVarietyToProduct = async (branchId, data) => {
  const response = (await axios.post(`${API_URL}/branch/${branchId}/product/variety`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'add-variety',
    varietyId: response?.id,
    productId: response?.productId ?? data?.productId,
    variety: response,
    ...(Array.isArray(response?.groups) ? { varietyGroups: response.groups } : {}),
  });
  return response;
};

export const updateVariety = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety/${id}`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'update-variety',
    varietyId: id,
    variety: response || { id, ...data },
  });
  return response;
};

export const updateVarietyPresentations = async (branchId, varietyId, data) => {
  const response = (await axios.patch(
    `${API_URL}/branch/${branchId}/product/variety/${varietyId}/presentations`,
    data,
    withAuth()
  )).data;
  invalidateProducts(branchId, {
    action: 'catalog-structure-updated',
    varietyId,
  });
  return response;
};

export const deleteVariety = async (branchId, id) => {
  const response = (await axios.delete(`${API_URL}/branch/${branchId}/product/variety/${id}`, withAuth())).data;
  invalidateProducts(branchId, { action: 'delete-variety', varietyId: id });
  return response;
};

const invalidateVarietyGroups = (branchId, productId, response = {}) => {
  invalidateProducts(branchId, {
    action: 'update-variety-groups',
    productId,
    varietyGroups: response?.groups || response?.VarietyGroups || [],
  });
};

export const getProductVarietyGroups = async (branchId, productId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/${productId}/variety-groups`, withAuth())).data;

export const createProductVarietyGroup = async (branchId, productId, payload = {}) => {
  const response = (await axios.post(
    `${API_URL}/branch/${branchId}/product/${productId}/variety-groups`,
    payload,
    withAuth()
  )).data;
  invalidateVarietyGroups(branchId, productId, response);
  return response;
};

export const updateProductVarietyGroup = async (branchId, productId, groupId, payload = {}) => {
  const response = (await axios.patch(
    `${API_URL}/branch/${branchId}/product/${productId}/variety-groups/${groupId}`,
    payload,
    withAuth()
  )).data;
  invalidateVarietyGroups(branchId, productId, response);
  return response;
};

export const deleteProductVarietyGroup = async (branchId, productId, groupId) => {
  const response = (await axios.delete(
    `${API_URL}/branch/${branchId}/product/${productId}/variety-groups/${groupId}`,
    withAuth()
  )).data;
  invalidateVarietyGroups(branchId, productId, response);
  return response;
};

export const reorderProductVarietyGroups = async (branchId, productId, groupIds = []) => {
  const response = (await axios.put(
    `${API_URL}/branch/${branchId}/product/${productId}/variety-groups/order`,
    { groupIds },
    withAuth()
  )).data;
  invalidateVarietyGroups(branchId, productId, response);
  return response;
};

export const replaceProductVarietyGroupVarieties = async (branchId, productId, groupId, varietyIds = []) => {
  const response = (await axios.put(
    `${API_URL}/branch/${branchId}/product/${productId}/variety-groups/${groupId}/varieties`,
    { varietyIds },
    withAuth()
  )).data;
  invalidateVarietyGroups(branchId, productId, response);
  return response;
};

export const markAsSold = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety/${id}/sold-out`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'variety-sold-out',
    varietyId: id,
    variety: response || { id, isSoldOut: true },
    isSoldOut: true,
  });
  return response;
};

export const markAsAvailable = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety/${id}/available`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'variety-available',
    varietyId: id,
    variety: response || { id, isSoldOut: false },
    isSoldOut: false,
  });
  return response;
};

export const markPriceAsSold = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-price/${id}/sold-out`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'price-sold-out',
    priceId: id,
    price: response || { id, isSoldOut: true },
    isSoldOut: true,
  });
  return response;
};

export const markPriceAsAvailable = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-price/${id}/available`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'price-available',
    priceId: id,
    price: response || { id, isSoldOut: false },
    isSoldOut: false,
  });
  return response;
};

// 💰 Variety Prices
export const addVarietyPriceToVariety = async (branchId, data) => {
  const response = (await axios.post(`${API_URL}/branch/${branchId}/product/variety-price`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'add-variety-price',
    priceId: response?.id,
    varietyId: response?.varietyId ?? data?.varietyId,
    price: response,
  });
  return response;
};

export const addVarietyFormat = async (branchId, data) => {
  const response = (await axios.post(`${API_URL}/branch/${branchId}/product/variety-format`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'add-variety-price',
    priceId: response?.id,
    varietyId: response?.varietyId ?? data?.varietyId,
    price: response,
  });
  return response;
};

export const updateVarietyFormat = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-format/${id}`, data, withAuth())).data;
  invalidateProducts(branchId, { action: 'update-variety-price', priceId: id, price: response });
  return response;
};

export const removeVarietyFormat = async (branchId, id) => {
  const response = (await axios.delete(`${API_URL}/branch/${branchId}/product/variety-format/${id}`, withAuth())).data;
  invalidateProducts(branchId, { action: 'delete-variety-price', priceId: id });
  return response;
};

export const updateVarietyPrice = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-price/${id}`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'update-variety-price',
    priceId: id,
    price: response || { id, ...data },
  });
  return response;
};

export const removeVarietyPrice = async (branchId, id) => {
  const response = (await axios.delete(`${API_URL}/branch/${branchId}/product/variety-price/${id}`, withAuth())).data;
  invalidateProducts(branchId, { action: 'delete-variety-price', priceId: id });
  return response;
};

export const getPriceByBarcode = async (branchId, barcode, productFormatCode) => {
  const params = new URLSearchParams();
  if (barcode) params.set('barcode', barcode);
  if (productFormatCode) {
    params.set('productFormatCode', productFormatCode);
    params.set('clientType', productFormatCode);
  }
  const query = params.toString();
  const suffix = query ? `?${query}` : '';
  return (await axios.get(`${API_URL}/branch/${branchId}/product/prices/by-barcode${suffix}`)).data;
};

export const previewVarietyFormatDrafts = async (branchId, data = {}) => (
  await axios.post(`${API_URL}/branch/${branchId}/product/variety-format-drafts/preview`, data, withAuth())
).data;

export const completePriceListFormat = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/price-list/formats/${id}/completion`, data, withAuth())).data;
  invalidateProducts(branchId, { action: 'catalog-structure-updated', priceIds: [id] });
  return response;
};
